import { NotiWarning } from '@ahamove/icons';

import { Insurance } from '@ahamove/icons/order';
import { SERVICE_REQUESTS } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { toast } from '@ahm/react-ui';
import {
  ActionIcon,
  Box,
  Button,
  CloseIcon,
  Group,
  Popover,
  Text,
  Title,
  UnstyledButton,
  rem,
} from '@mantine/core';
import { useClickOutside, useDisclosure } from '@mantine/hooks';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import InsuranceOptions from './insurance-options';
import ChevronBlack from '@/assets/icons/chevron-black-icon.svg';
import IconToastInfo from '@/assets/icons/toast-info-icon.svg';
import IconToastSuccess from '@/assets/icons/toast-success-icon.svg';
import InfoTooltip from '@/components/info-tooltip';
import CustomComponent from '@/components/modals/modal-custom-drawer';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { findInsuranceInRequests } from '@/lib/utils';
import { useBookingStore } from '@/stores/booking/booking-store-provider';
import { InsurancePackageType } from '@/types/booking';

const InsuranceItem = () => {
  const isMobile = useIsMobile();
  const tBooking = useTranslations('booking');
  const [opened, { close, open }] = useDisclosure(false);
  const ref = useClickOutside(() => close());

  const {
    selectedService,
    insuranceValue,
    serviceRequests,
    setServiceRequest,
  } = useBookingStore((state) => state);

  const insuranceRequest = useMemo(() => {
    return selectedService ? findInsuranceInRequests(selectedService) : null;
  }, [selectedService]);

  const specialRequestList = useMemo(() => {
    if (!selectedService) return [];
    return selectedService?.requests ?? [];
  }, [selectedService]);

  const mainRequestList = useMemo(() => {
    return specialRequestList.filter(
      (request) => request.type === SERVICE_REQUESTS.COD_INSURANCE
    );
  }, [specialRequestList]);

  const noneInsuranceValue = useMemo(() => {
    return insuranceValue === InsurancePackageType.None ? true : false;
  }, [insuranceValue]);

  const handleChangeRequestParams = useCallback(
    (id?: string, value: string | number | boolean | undefined = undefined) => {
      if (!id || !selectedService?._id) return;

      const param = { ...serviceRequests };
      if (value === undefined || (serviceRequests && id in serviceRequests)) {
        delete param[id as keyof typeof serviceRequests];

        toast.info({
          title: tBooking('insurance.toast.remove_insurance'),
          withIcon: true,
          withCloseButton: false,
          icon: <IconToastInfo />,
        });
      } else {
        param[id as keyof typeof serviceRequests] = value;

        toast.success({
          title: tBooking('insurance.toast.add_insurance'),
          withIcon: true,
          withCloseButton: false,
          icon: <IconToastSuccess />,
        });
      }
      setServiceRequest(param);
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedService?._id, serviceRequests, setServiceRequest]
  );

  const handleOpenTooltip = () => {
    const benefitUrl = insuranceRequest?.benefit_url;

    if (!benefitUrl) return;

    window.open(benefitUrl, '_blank');
  };

  if (!mainRequestList || mainRequestList.length === 0) {
    return;
  }

  return (
    <>
      {isMobile ? (
        <>
          <Button
            styles={{
              root: {
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              },
              inner: {
                color: 'var(--mantine-color-neutral-6)',
              },
              label: {
                flex: '1 1 auto',
              },
            }}
            justify="space-between"
            size="sm"
            mt={-16}
            mx={-16}
            radius={0}
            onClick={open}
            className={cn('!bg-[#E4F2FF]', {
              '!bg-yellow-10': noneInsuranceValue,
            })}
            leftSection={
              noneInsuranceValue ? (
                <NotiWarning className="size-6" />
              ) : (
                <Insurance className="size-6" />
              )
            }
            rightSection={
              <>
                {noneInsuranceValue ? (
                  <UnstyledButton>
                    <Text c="primary" fw={700} fz={14}>
                      {tBooking('insurance.choose')}
                    </Text>
                  </UnstyledButton>
                ) : (
                  <ActionIcon
                    w={32}
                    h={32}
                    variant="transparent"
                    color="neutral.7"
                  >
                    <ChevronBlack className="size-6" />
                  </ActionIcon>
                )}
              </>
            }
          >
            <Text fz={{ base: 'sm', sm: 'md' }} fw={500} c="neutral.6">
              {noneInsuranceValue
                ? tBooking('insurance.choose_insurance')
                : tBooking('insurance.insurance_applied')}
            </Text>
          </Button>
          <CustomComponent
            title={tBooking('insurance.declare_item_value')}
            isOpen={opened}
            onClose={close}
            optionStyle={{
              headerStyle: {
                minHeight: rem(56),
                width: '100%',
                borderBottom: '1px solid var(--mantine-color-neutral-0)',
              },
              bodyStyle: {
                minHeight: rem(206),
                padding: 0,
              },
            }}
            rigthIconTitle={
              <Box mr={rem(-8)}>
                <InfoTooltip label={tBooking('insurance.declare_item_value')} />
              </Box>
            }
            onClickRigthIcon={handleOpenTooltip}
          >
            <InsuranceOptions
              handleChangeRequestParams={handleChangeRequestParams}
              request={mainRequestList[0]}
              close={close}
            />
          </CustomComponent>
        </>
      ) : (
        <Popover
          styles={{
            dropdown: {
              filter: 'drop-shadow(0px 16px 48px rgba(0, 0, 0, 0.08))',
              borderRadius: 12,
              background: 'var(--mantine-color-neutral-0)',
              padding: 0,
            },
            arrow: {
              backgroundColor: 'white',
            },
          }}
          zIndex={200}
          width={350}
          opened={opened}
          closeOnClickOutside={true}
          clickOutsideEvents={['mouseup', 'touchend']}
          offset={{ mainAxis: 8, crossAxis: 0 }}
          position="right"
          withArrow
          arrowSize={16}
          shadow="md"
        >
          <Popover.Target>
            <Button
              styles={{
                root: {
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                },
                inner: {
                  color: 'var(--mantine-color-neutral-6)',
                },
              }}
              justify="space-between"
              size="sm"
              mt={-16}
              mx={-16}
              radius={0}
              onClick={open}
              className={cn('!bg-[#E4F2FF]', {
                '!bg-yellow-10': noneInsuranceValue,
              })}
              rightSection={
                <>
                  {noneInsuranceValue ? (
                    <UnstyledButton mr={4}>
                      <Text c="primary" fw={700} fz={14}>
                        {tBooking('insurance.choose')}
                      </Text>
                    </UnstyledButton>
                  ) : (
                    <ChevronBlack className="size-6" fill="#000" />
                  )}
                </>
              }
            >
              <Group ml={-4} justify="flex-start">
                {noneInsuranceValue ? (
                  <NotiWarning className="size-6" />
                ) : (
                  <Insurance className="size-6" />
                )}
                <Text fz={16} c="neutral.7" fw={500} truncate>
                  {noneInsuranceValue
                    ? tBooking('insurance.choose_insurance')
                    : tBooking('insurance.insurance_applied')}
                </Text>
              </Group>
            </Button>
          </Popover.Target>
          <Popover.Dropdown ref={ref}>
            <Group
              justify="space-between"
              px="md"
              py="sm"
              styles={{
                root: {
                  background: 'white',
                  borderBottom: '1px solid var(--mantine-color-neutral-1)',
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                },
              }}
            >
              <Group
                wrap="nowrap"
                align="flex-start"
                gap={4}
                style={{ flex: '1 1 auto !important' }}
              >
                <Title order={4}>
                  {tBooking('insurance.declare_item_value')}
                </Title>

                <InfoTooltip
                  multiline
                  maw={300}
                  className="self-center"
                  onClick={handleOpenTooltip}
                  label={tBooking('insurance.declare_item_value')}
                />
              </Group>
              <ActionIcon
                mr={-8}
                variant="transparent"
                w={32}
                h={32}
                c="neutral"
                onClick={() => {
                  close();
                }}
              >
                <CloseIcon className="size-6" />
              </ActionIcon>
            </Group>
            <InsuranceOptions
              handleChangeRequestParams={handleChangeRequestParams}
              request={mainRequestList[0]}
              close={close}
            />
          </Popover.Dropdown>
        </Popover>
      )}
    </>
  );
};

export default InsuranceItem;
